import { AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { mainConfig } from '@config/main.config';
import { Articolo } from '@models/articolo.model';
import { Categoria } from '@models/categoria.model';
import { User } from '@models/user.model';
import { AIService } from '@services/ai.service';
import { ArticoliService } from '@services/articoli.service';
import { AuthService } from '@services/auth.service';
import { CategorieService } from '@services/categorie.service';
import { UsersService } from '@services/users.service';
import { UtilsService } from '@services/utils.service';
import { iniziali } from '@services/iniziali.util';
import { PageRequest } from '@sinapsys/ngx-crud';
import { ConfermaService } from '@shared/conferma/conferma-dialog.component';
import { ColumnDefinition } from '@shared/data-table/column-definition';
import { DataTableComponent } from '@shared/data-table/data-table.component';
import { FiltriArticoli, condizioniArticoli } from '@shared/utils/filtri-articoli.util';
import { StatoArticolo, statoArticolo } from '@shared/utils/stato-articolo.util';
import { dataOraPiena, tempoRelativo } from '@shared/utils/tempo-relativo.util';
import { Observable, Subscription, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { PageComponent } from '../../../layout/page/page.component';
import { COMMON_APP_CONFIG } from 'src/app/core/tokens/common-config.token';

/** Come si chiamano gli stati nella barra dei filtri, al plurale. */
const ETICHETTE_STATO: Record<StatoArticolo['codice'], string> = {
  pubblicato: 'Pubblicati',
  programmato: 'Programmati',
  bozza: 'Bozze',
};

/**
 * Elenco degli articoli.
 *
 * L'editor sta nella sua pagina (`ArticoloComponent`): prima viveva in una
 * finestra modale dichiarata in questo stesso file, che con l'elenco arrivava
 * a millecinquecento righe e due mestieri diversi.
 */
@Component({
  selector: 'app-articoli',
  standalone: true,
  imports: [DataTableComponent, PageComponent, mainConfig.material, mainConfig.formModules, AsyncPipe],
  templateUrl: './articoli.component.html',
})
export class ArticoliComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent, { static: false }) table!: DataTableComponent<Articolo>;

  // Celle dell'elenco. `static: true` perche' servono a costruire le colonne
  // in ngOnInit, prima che la tabella disegni qualcosa.
  @ViewChild('titoloCella', { static: true }) titoloCella!: TemplateRef<unknown>;
  @ViewChild('statoCella', { static: true }) statoCella!: TemplateRef<unknown>;
  @ViewChild('segnaliCella', { static: true }) segnaliCella!: TemplateRef<unknown>;
  @ViewChild('categoriaCella', { static: true }) categoriaCella!: TemplateRef<unknown>;
  @ViewChild('autoreCella', { static: true }) autoreCella!: TemplateRef<unknown>;
  @ViewChild('dataCella', { static: true }) dataCella!: TemplateRef<unknown>;
  @ViewChild('azioniCella', { static: true }) azioniCella!: TemplateRef<unknown>;

  columnDefinitions: ColumnDefinition<Articolo>[] = [];
  searchSubscription?: Subscription;

  // --- Filtri dell'elenco ---------------------------------------------------
  //
  // Prima erano quattro campi Material sempre aperti in un blocco centrato con
  // 40px di aria sopra e sotto, piu' i pulsanti FILTRA e RESET: bisognava
  // premere per vedere l'effetto, e nulla diceva quali filtri fossero attivi.
  readonly filtroTitolo = new FormControl<string>('', { nonNullable: true });
  readonly filtroStato = new FormControl<StatoArticolo['codice'] | null>(null);
  readonly filtroCategoria = new FormControl<Categoria | string | null>(null);
  readonly filtroAutore = new FormControl<User | string | null>(null);
  readonly filtroDa = new FormControl<string | null>(null);
  readonly filtroA = new FormControl<string | null>(null);
  readonly filtroId = new FormControl<number | null>(null);

  /** Su telefono i filtri stanno chiusi dietro un pulsante. */
  readonly filtriAperti = signal(false);

  readonly caricamentoElenco = signal(false);
  readonly erroreElenco = signal<string | null>(null);
  readonly totaleElenco = signal<number | null>(null);

  autoriDataSource!: Observable<User[]>;
  categorieDataSource!: Observable<Categoria[]>;

  private readonly sottoscrizioniFiltri = new Subscription();

  constructor(
    public utilsService: UtilsService,
    private articoliService: ArticoliService,
    private categorieService: CategorieService,
    public authService: AuthService,
    private usersService: UsersService,
    private aiService: AIService,
    private router: Router,
    private route: ActivatedRoute,
    private conferma: ConfermaService,
    @Inject(COMMON_APP_CONFIG) private mainConfig: Required<{ api: { apiBaseUrl: string } }>
  ) {}

  ngOnDestroy(): void {
    this.sottoscrizioniFiltri.unsubscribe();
  }

  ngOnInit(): void {
    // Le tre colonne che dicevano "Si" e "No" (Esterno, PPN, Pubblicato)
    // diventano segnali visivi: un badge di stato, una puntina, un'etichetta.
    this.columnDefinitions = [
      { id: 'approvato', header: 'Stato', sortable: true, template: this.statoCella, width: '132px' },
      { id: 'titolo', header: 'Titolo', sortable: true, template: this.titoloCella },
      { id: 'categoria', header: 'Categoria', sortable: true, template: this.categoriaCella, width: '160px' },
      { id: 'autore', header: 'Autore', sortable: true, template: this.autoreCella, width: '180px' },
      { id: 'data', header: 'Pubblicazione', sortable: true, template: this.dataCella, width: '150px' },
      { id: 'evidenza', header: 'Segnali', sortable: false, template: this.segnaliCella, width: '130px' },
      { id: 'id', header: 'ID', sortable: true, width: '90px', nascondiSuMobile: true },
      { id: '_', header: '', template: this.azioniCella, width: '56px', allineamento: 'fine' },
    ];

    this.leggiFiltriDaUrl();
    this.ascoltaFiltri();

    this.autoriDataSource = this.filtroAutore.valueChanges.pipe(
      debounceTime(350),
      switchMap((cercato) =>
        typeof cercato === 'string' && cercato.trim() ? this.usersService.getAutocomplete(cercato) : of([])
      )
    );

    this.categorieDataSource = this.filtroCategoria.valueChanges.pipe(
      debounceTime(350),
      switchMap((cercato) =>
        typeof cercato === 'string' && cercato.trim() ? this.categorieService.getAutocomplete(cercato) : of([])
      )
    );
  }

  search(mantieniPagina?: boolean) {
    if (!this.table) return;

    // Al primo giro l'elenco parte dal piu' recente. Prima l'ordinamento
    // restava vuoto e decideva il backend, con `createdAt` come ripiego.
    if (!this.table.sort.active) {
      this.table.sort.active = 'id';
      this.table.sort.direction = 'desc';
    }

    if (!mantieniPagina) this.table.paginator.pageIndex = 0;

    const pageRequest: PageRequest = new PageRequest(
      this.table.paginator.pageIndex,
      this.table.paginator.pageSize,
      this.table.sort.direction,
      this.table.sort.active
    );

    this.searchSubscription?.unsubscribe();
    this.caricamentoElenco.set(true);
    this.erroreElenco.set(null);

    this.searchSubscription = this.articoliService.search(condizioniArticoli(this.filtriCorrenti()), pageRequest).subscribe({
      next: (res) => {
        res.items = res.items.map((item) => new Articolo(item));
        this.table.populate(res);
        this.totaleElenco.set(res.total);
        this.caricamentoElenco.set(false);
      },
      error: (err) => {
        this.caricamentoElenco.set(false);
        this.totaleElenco.set(null);
        // Il motivo resta dentro l'elenco: la notifica passa e non spiega
        // perche' la tabella sia vuota.
        this.erroreElenco.set(err?.error?.message || err?.error?.messaggio || err?.message || 'Errore inatteso');
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  // --- Filtri ---------------------------------------------------------------

  private filtriCorrenti(): FiltriArticoli {
    const categoria = this.filtroCategoria.value;
    const autore = this.filtroAutore.value;

    return {
      id: this.filtroId.value ?? null,
      titolo: this.filtroTitolo.value,
      stato: this.filtroStato.value,
      // Finche' non si sceglie una voce il controllo contiene il testo
      // digitato, che non e' un filtro: non si cerca per nome di categoria.
      categoriaId: typeof categoria === 'object' && categoria ? categoria.id : null,
      autoreId: typeof autore === 'object' && autore ? autore.id : null,
      da: this.inizioGiorno(this.filtroDa.value),
      a: this.fineGiorno(this.filtroA.value),
    };
  }

  private inizioGiorno(giorno: string | null): number | null {
    if (!giorno) return null;
    return Math.floor(new Date(`${giorno}T00:00:00`).getTime() / 1000);
  }

  private fineGiorno(giorno: string | null): number | null {
    if (!giorno) return null;
    return Math.floor(new Date(`${giorno}T23:59:59`).getTime() / 1000);
  }

  private ascoltaFiltri(): void {
    // Il titolo aspetta che si finisca di scrivere; il resto sono scelte, e
    // una scelta vale subito.
    this.sottoscrizioniFiltri.add(
      this.filtroTitolo.valueChanges
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe(() => this.applicaFiltri())
    );

    this.sottoscrizioniFiltri.add(this.filtroStato.valueChanges.subscribe(() => this.applicaFiltri()));
    this.sottoscrizioniFiltri.add(this.filtroDa.valueChanges.subscribe(() => this.applicaFiltri()));
    this.sottoscrizioniFiltri.add(this.filtroA.valueChanges.subscribe(() => this.applicaFiltri()));

    this.sottoscrizioniFiltri.add(
      this.filtroId.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.applicaFiltri())
    );

    // Categoria e autore filtrano solo quando si sceglie una voce dall'elenco,
    // oppure quando si svuota il campo: il testo digitato a metà non è un
    // filtro.
    this.sottoscrizioniFiltri.add(
      this.filtroCategoria.valueChanges.subscribe((valore: Categoria | string | null) => {
        if (typeof valore === 'object' || !valore) this.applicaFiltri();
      })
    );
    this.sottoscrizioniFiltri.add(
      this.filtroAutore.valueChanges.subscribe((valore: User | string | null) => {
        if (typeof valore === 'object' || !valore) this.applicaFiltri();
      })
    );
  }

  private applicaFiltri(): void {
    this.scriviFiltriInUrl();
    this.search();
  }

  /**
   * I filtri stanno anche nell'indirizzo: un elenco filtrato si puo' mandare a
   * un collega, e tornando indietro dall'articolo lo si ritrova com'era.
   */
  private scriviFiltriInUrl(): void {
    const categoria = this.filtroCategoria.value;
    const autore = this.filtroAutore.value;

    const queryParams: Record<string, string | number | null> = {
      titolo: this.filtroTitolo.value?.trim() || null,
      stato: this.filtroStato.value,
      categoria: typeof categoria === 'object' && categoria ? categoria.id : null,
      autore: typeof autore === 'object' && autore ? autore.id : null,
      da: this.filtroDa.value || null,
      a: this.filtroA.value || null,
      id: this.filtroId.value || null,
    };

    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  private leggiFiltriDaUrl(): void {
    const parametri = this.route.snapshot.queryParamMap;
    const opzioni = { emitEvent: false } as const;

    this.filtroTitolo.setValue(parametri.get('titolo') ?? '', opzioni);
    this.filtroStato.setValue((parametri.get('stato') as StatoArticolo['codice'] | null) ?? null, opzioni);
    this.filtroDa.setValue(parametri.get('da'), opzioni);
    this.filtroA.setValue(parametri.get('a'), opzioni);
    this.filtroId.setValue(parametri.get('id') ? Number(parametri.get('id')) : null, opzioni);

    // Di categoria e autore l'indirizzo porta solo l'identificativo: il nome da
    // mostrare nel campo arriva con una lettura mirata.
    const categoria = parametri.get('categoria');
    if (categoria) {
      this.categorieService
        .search({ id: Number(categoria) }, new PageRequest(0, 1))
        .subscribe((res) => this.filtroCategoria.setValue(new Categoria(res.items[0]), opzioni));
    }

    const autore = parametri.get('autore');
    if (autore) {
      this.usersService
        .search({ id: Number(autore) }, new PageRequest(0, 1))
        .subscribe((res) => this.filtroAutore.setValue(new User(res.items[0]), opzioni));
    }

  }

  filtriAttivi(): { chiave: string; etichetta: string }[] {
    const attivi: { chiave: string; etichetta: string }[] = [];
    const categoria = this.filtroCategoria.value;
    const autore = this.filtroAutore.value;

    if (this.filtroTitolo.value?.trim()) attivi.push({ chiave: 'titolo', etichetta: `Titolo: ${this.filtroTitolo.value.trim()}` });
    if (this.filtroStato.value) attivi.push({ chiave: 'stato', etichetta: `Stato: ${ETICHETTE_STATO[this.filtroStato.value]}` });
    if (typeof categoria === 'object' && categoria) attivi.push({ chiave: 'categoria', etichetta: `Categoria: ${categoria.nome}` });
    if (typeof autore === 'object' && autore) attivi.push({ chiave: 'autore', etichetta: `Autore: ${autore.blogger}` });
    if (this.filtroDa.value) attivi.push({ chiave: 'da', etichetta: `Dal ${this.filtroDa.value}` });
    if (this.filtroA.value) attivi.push({ chiave: 'a', etichetta: `Al ${this.filtroA.value}` });
    if (this.filtroId.value) attivi.push({ chiave: 'id', etichetta: `ID ${this.filtroId.value}` });

    return attivi;
  }

  togliFiltro(chiave: string): void {
    switch (chiave) {
      case 'titolo':
        this.filtroTitolo.setValue('');
        return;
      case 'stato':
        this.filtroStato.setValue(null);
        return;
      case 'categoria':
        this.filtroCategoria.setValue(null);
        return;
      case 'autore':
        this.filtroAutore.setValue(null);
        return;
      case 'da':
        this.filtroDa.setValue(null);
        return;
      case 'a':
        this.filtroA.setValue(null);
        return;
      case 'id':
        this.filtroId.setValue(null);
        return;
    }
  }

  azzeraFiltri(): void {
    const zitto = { emitEvent: false } as const;
    this.filtroTitolo.setValue('', zitto);
    this.filtroStato.setValue(null, zitto);
    this.filtroCategoria.setValue(null, zitto);
    this.filtroAutore.setValue(null, zitto);
    this.filtroDa.setValue(null, zitto);
    this.filtroA.setValue(null, zitto);
    this.filtroId.setValue(null, zitto);
    this.applicaFiltri();
  }

  // --- Contenuto delle celle ------------------------------------------------

  statoDi(articolo: Articolo): StatoArticolo {
    return statoArticolo(articolo);
  }

  quando(secondi: number | null | undefined): string {
    return tempoRelativo(secondi);
  }

  dataCompleta(secondi: number | null | undefined): string {
    return dataOraPiena(secondi);
  }

  inizialiAutore(articolo: Articolo): string {
    return iniziali(articolo.autore?.blogger);
  }

  /** Nome di chi ha mandato l'articolo dall'esterno, se c'e'. */
  mittenteEsterno(articolo: Articolo): string | null {
    const nome = [articolo.nome_esterno, articolo.cognome_esterno].filter((p) => p?.trim()).join(' ').trim();
    return nome || null;
  }

  nomeCategoria(categoria: Categoria | string | null): string {
    return typeof categoria === 'object' && categoria ? categoria.nome : (categoria ?? '');
  }

  nomeAutore(autore: User | string | null): string {
    return typeof autore === 'object' && autore ? autore.blogger : (autore ?? '');
  }

  sottotitolo(): string {
    const totale = this.totaleElenco();
    if (totale === null) return '';
    if (totale === 0) return 'Nessun articolo con questi filtri';
    return totale === 1 ? '1 articolo' : `${totale.toLocaleString('it-IT')} articoli`;
  }

  etichettaVuoto(): string {
    return this.filtriAttivi().length
      ? 'Nessun articolo con questi filtri. Prova a togliere qualche filtro.'
      : 'Nessun articolo in archivio.';
  }

  // --- Azioni di riga -------------------------------------------------------
  //
  // Prima stavano tutte in fondo alla modale: per aggiornare la cache o
  // condividere sui social bisognava aprire l'articolo.

  /**
   * Guardare la pagina e rigenerarne la cache ha senso solo se la pagina c'e'.
   * Dal 29/09/2026 una bozza sul sito risponde 404, e un programmato non e'
   * ancora uscito: come nell'editor, le due voci valgono solo per gli
   * articoli online.
   */
  azioniSito(articolo: Articolo): boolean {
    return statoArticolo(articolo).codice === 'pubblicato';
  }

  apriSulSito(articolo: Articolo, aggiornaCache = false): void {
    if (!this.azioniSito(articolo)) return;
    const indirizzo = `https://www.infooggi.it/articolo/${articolo.permalink}/${articolo.id}`;
    window.open(aggiornaCache ? `${indirizzo}/f` : indirizzo, '_blank');
  }

  pubblicaSuiSocial(articolo: Articolo): void {
    // Come nell'editor: su una bozza o su un articolo programmato la pagina
    // pubblica non risponde 200 e il backend rifiuta con un 409.
    if (statoArticolo(articolo).codice !== 'pubblicato') return;

    this.aiService
      .socialPublish(`https://www.infooggi.it/articolo/${articolo.permalink}/${articolo.id}`, String(articolo.id))
      .subscribe({
        next: () => {
          this.utilsService.notificationsService.showNotification('', 'Articolo condiviso con successo!', 'success');
          this.search(true);
        },
        error: (err) => {
          // Il backend risponde 409 quando la pagina pubblica non risponde 200:
          // in quel caso i social memorizzerebbero un'anteprima con "Pagina non
          // trovata".
          const messaggio =
            err?.error?.messaggio || "Condivisione non riuscita. Controlla che l'articolo sia raggiungibile sul sito e riprova.";
          this.utilsService.notificationsService.showNotification('', messaggio, 'error');
        },
      });
  }

  async eliminaArticolo(articolo: Articolo): Promise<void> {
    if (!this.authService.user?.can('owners')) return;

    const procedi = await this.conferma.chiedi({
      titolo: 'Eliminare questo articolo?',
      testo: `"${articolo.titolo}" verra' rimosso definitivamente. L'operazione non si puo' annullare.`,
      conferma: 'Elimina',
      distruttiva: true,
    });
    if (!procedi) return;

    this.articoliService.delete(articolo.id).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Articolo eliminato con successo', 'success');
        this.search(true);
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }


  // --- Apertura dell'editor -------------------------------------------------

  new() {
    this.router.navigate(['/gestione-articoli/articoli/nuovo']);
  }

  edit(articolo: Articolo) {
    this.router.navigate(['/gestione-articoli/articoli', articolo.id]);
  }
}
